import { useQuery, useQueryClient } from '@tanstack/react-query'
import Avatar from 'boring-avatars'
import dayjs from 'dayjs'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import ArrowRightIcon from '@/assets/icons/arrow-right.svg?react'
import CheckIcon from '@/assets/icons/check.svg?react'
import MoreIcon from '@/assets/icons/more.svg?react'
import TeamsIcon from '@/assets/icons/teams.svg?react'
import GlowsIcon from '@/assets/images/logo-icon.svg?react'
import { Trans } from '@/components'
import { ActionMenu } from '@/components/base/ActionMenu'
import { Button } from '@/components/base/Button'
import { Divider } from '@/components/base/Divider'
import { Modal } from '@/components/base/Modal'
import { message } from '@/components/base/Notification'
import { PrivilegeType, TeamRole } from '@/consts/team'
import { useAppContext } from '@/hooks/useAppContext'
import { usePersonalConsoleVisibility } from '@/hooks/usePersonalConsoleVisibility'
import { Team } from '@/models/team'
import { TeamManagePlanFlow } from '@/pages/teams/components/TeamManagePlanFlow'
import { TeamRenewModal } from '@/pages/teams/components/TeamRenewModal'
import { useTeamStore } from '@/stores/team'
import { LOGIN_TOKEN_COOKIE_EXPIRY_DAYS } from '@/utils/auth'
import { broadcastPlatformContextSwitched, broadcastTeamContextSwitched } from '@/utils/contextSync'
import { cn } from '@/utils/style'

import { UserDropdownItem } from './UserDropdownItem'

interface TeamSwitcherProps {
  onJoinTeam: () => void
  onCreateTeam: () => void
}

export function TeamSwitcher({ onJoinTeam, onCreateTeam }: TeamSwitcherProps) {
  const { t } = useTranslation()
  const [showTeamsMenu, setShowTeamsMenu] = useState(false)
  const [isLoadingTeam, setIsLoadingTeam] = useState(false)
  const [unbindingTeam, setUnbindingTeam] = useState<string | null>(null)
  const [renewingTeam, setRenewingTeam] = useState<Team | null>(null)
  const [showRenewModal, setShowRenewModal] = useState(false)
  const [managingTeam, setManagingTeam] = useState<Team | null>(null)
  const [showManagePlanFlow, setShowManagePlanFlow] = useState(false)
  const teamsTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const teamStore = useTeamStore()
  const queryClient = useQueryClient()

  // Helper function to get translated role label
  const getRoleLabel = (role: TeamRole): string => {
    if (role === TeamRole.Owner) return t('team.role.owner')
    if (role === TeamRole.Admin) return t('team.role.admin')
    return t('team.role.member')
  }

  // Helper function to format date as YYYY-MM-DD
  const formatDate = (date: Date): string => {
    return date.toISOString().split('T')[0]
  }

  // Use unified context hook for all context-related state
  const { isInPlatform, currentTeamId, hasPlatformToken, hasTeamToken } = useAppContext()

  // Fetch team list if user has either platform or team token
  const shouldFetchTeams = hasPlatformToken || hasTeamToken

  const { data: teamsData } = useQuery({
    queryKey: ['teamList'],
    queryFn: () => API.getTeamList({ page: 1, per_page: 100 }),
    enabled: shouldFetchTeams,
  })

  const teams: Team[] = (teamsData?.teams ?? []).map((item) => new Team(item))
  const hasBoundTeams = teams.length > 0

  // Determine Personal Console visibility using unified hook
  const shouldShowPersonalConsole = usePersonalConsoleVisibility(hasBoundTeams)

  const handleSwitchToPlatform = () => {
    setShowTeamsMenu(false)
    teamStore.clearTeamContext()
    broadcastPlatformContextSwitched('/create')
    window.location.replace('/create')
  }

  const handleTeamsMouseEnter = () => {
    if (teamsTimeoutRef.current) {
      clearTimeout(teamsTimeoutRef.current)
    }
    setShowTeamsMenu(true)
  }

  const handleTeamsMouseLeave = () => {
    teamsTimeoutRef.current = setTimeout(() => {
      setShowTeamsMenu(false)
    }, 100)
  }

  const handleJoinTeamInMenu = () => {
    onJoinTeam()
    setShowTeamsMenu(false)
  }

  const handleCreateTeamInMenu = () => {
    onCreateTeam()
    setShowTeamsMenu(false)
  }

  const switchToTeam = async (team: Team) => {
    const primaryTeamData = teamStore.primaryTeamTokenData
    const targetPath = isInPlatform ? '/team/home' : window.location.pathname

    // Check if switching back to primary team with valid token
    if (
      primaryTeamData &&
      primaryTeamData.teamId === team.id &&
      primaryTeamData.userName === team.user.name &&
      primaryTeamData.expiryTime > Date.now()
    ) {
      teamStore.setTeamContext(primaryTeamData, LOGIN_TOKEN_COOKIE_EXPIRY_DAYS)
      sessionStorage.setItem('__team_switching__', '1')
      broadcastTeamContextSwitched(targetPath, team.id, true)
      window.location.replace(targetPath)
      return
    }

    // Request new 15-minute token for non-primary team or expired primary token
    const res = await API.getTeamToken({
      privilege_type: PrivilegeType.TeamToken,
      source_id: team.id,
    })

    const { TEAM_TOKEN_EXPIRY_MS } = await import('@/utils/auth')
    const expiryTime = Date.now() + TEAM_TOKEN_EXPIRY_MS
    teamStore.setTeamContext({
      token: `Bearer ${res.token}`,
      teamId: team.id,
      teamResID: team.resID,
      expiryTime,
      userName: team.user.name,
    })

    sessionStorage.setItem('__team_switching__', '1')
    broadcastTeamContextSwitched(targetPath, team.id, true)
    window.location.replace(targetPath)
  }

  const handleTeamClick = async (team: Team) => {
    if (isLoadingTeam) return

    setIsLoadingTeam(true)
    setShowTeamsMenu(false)

    try {
      await switchToTeam(team)
    } catch (error) {
      console.error('Failed to switch to team:', error)
      message.error(t('team.messages.switchFailed'))
      setIsLoadingTeam(false)
    }
  }

  const handleUnbindTeam = async (team: Team) => {
    if (unbindingTeam === team.resID) return

    try {
      await Modal.confirm({
        title: (
          <Trans i18nKey="dialogs.unbind.title" ns="team">
            Unlink Team
          </Trans>
        ),
        content: (
          <Trans i18nKey="dialogs.unbind.content" ns="team">
            Are you sure you want to unlink{' '}
            <span className="text-highlight">{{ name: team.name } as never}</span>
          </Trans>
        ),
        confirmBtnCfg: {
          type: 'danger',
          text: (
            <Trans i18nKey="actions.unlinkTeam" ns="team">
              Unlink
            </Trans>
          ),
        },
      })

      setUnbindingTeam(team.resID)
      await API.unbindTeamMember({ res_id: team.resID })
      message.success(t('team.messages.unbindSuccess'))
      queryClient.invalidateQueries({ queryKey: ['teamList'] })
    } catch (error) {
      if (error instanceof Error) {
        console.error('Failed to unbind team:', error)
        message.error(t('team.messages.unbindFailed'))
      }
    } finally {
      setUnbindingTeam(null)
    }
  }

  return (
    <div
      className="relative"
      onMouseEnter={handleTeamsMouseEnter}
      onMouseLeave={handleTeamsMouseLeave}
    >
      {/* Teams Menu Item */}
      <UserDropdownItem
        icon={TeamsIcon}
        label={<Trans i18nKey="header.userDropdown.teams">Platform Teams</Trans>}
        active={showTeamsMenu}
        trailing={<ArrowRightIcon className="h-4 w-4 shrink-0 text-tertiary" />}
      />

      {/* Teams Secondary Menu */}
      <div
        className={cn(
          'absolute pt-4 top-0 -left-4 -translate-x-full w-[400px] h-[400px] bg-panel-white rounded-xl shadow-2xl overflow-hidden flex flex-col',
          showTeamsMenu ? 'flex' : 'hidden',
        )}
      >
        {shouldShowPersonalConsole && (
          <>
            <div
              className={cn(
                'flex items-center gap-x-4 px-4 py-2 cursor-pointer hover:bg-panel-gray-100 transition-colors',
                isInPlatform && 'bg-highlight/10 text-highlight',
              )}
              onClick={handleSwitchToPlatform}
            >
              <div className="flex-1 flex gap-x-3 items-center">
                <GlowsIcon className="w-8 h-8 text-secondary" />
                <div className="t-x-sb">
                  <Trans i18nKey="header.userDropdown.personalConsole">Personal Console</Trans>
                </div>
              </div>
              <div>{isInPlatform && <CheckIcon className="w-5 h-5 text-highlight" />}</div>
            </div>

            <div className="px-4">
              <Divider className="my-2" />
            </div>
          </>
        )}

        {/* Content area - flex-1 will take remaining space */}

        {teams.length === 0 ? (
          <div className="flex-1 flex items-center justify-center p-4">
            <div className="t-x-sb text-tertiary">
              <Trans i18nKey="header.userDropdown.noTeamYet">No team yet</Trans>
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            {teams.map((team) => {
              const isUnbinding = unbindingTeam === team.resID
              const canUnbind = team.user.role !== TeamRole.Owner && isInPlatform
              const isCurrentTeam = team.id === currentTeamId

              return (
                <div
                  key={team.resID}
                  className={cn(
                    'px-4 py-2 flex gap-x-3 items-center transition-colors',
                    isUnbinding && 'cursor-not-allowed opacity-50',
                    team.isExpired && 'cursor-not-allowed',
                    !isUnbinding &&
                      !isCurrentTeam &&
                      !team.isExpired &&
                      'cursor-pointer hover:bg-panel-gray-100',
                    isCurrentTeam && 'bg-highlight/10 text-highlight',
                  )}
                  onClick={() =>
                    !isUnbinding && !isCurrentTeam && !team.isExpired && handleTeamClick(team)
                  }
                >
                  <div>
                    <Avatar
                      size={32}
                      name={team.name}
                      variant="beam"
                      colors={['#525866', '#7B8394', '#A0A8B8']}
                    />
                  </div>
                  <div className="flex flex-col flex-1 min-w-0">
                    <div className="t-x-sb flex items-center gap-2">
                      <span className="truncate">{team.name}</span>
                      {team.isExpired && (
                        <span className="bg-error/10 text-error t-xs shrink-0 rounded-full px-2 py-0.5">
                          {t('team.expired')}
                        </span>
                      )}
                    </div>
                    <div className="t-xs text-tertiary flex items-center gap-2">
                      <span>{getRoleLabel(team.user.role)}</span>
                      {team.orderDeadline && (
                        <>
                          <span>|</span>
                          {team.isExpired ? (
                            <span className="text-error">
                              <Trans
                                i18nKey="team.dataPurgeOn"
                                values={{
                                  date: dayjs(team.orderDeadline)
                                    .add(30, 'day')
                                    .format('YYYY-MM-DD'),
                                }}
                              >
                                Data purge on {'{{date}}'}
                              </Trans>
                            </span>
                          ) : (
                            <span>{formatDate(team.orderDeadline)}</span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    {isCurrentTeam && <CheckIcon className="w-5 h-5 text-highlight" />}
                    <ActionMenu
                      placement="bottomLeft"
                      popupContainer="body"
                      trigger="hover"
                      icon={<MoreIcon className="w-5 h-5 text-tertiary cursor-pointer" />}
                      items={[
                        ...(isInPlatform
                          ? [
                              {
                                label: t('actions.renewPlan', { ns: 'team' }),
                                onClick: () => {
                                  setRenewingTeam(team)
                                  setShowRenewModal(true)
                                },
                                disabled: !team.order,
                              },
                              {
                                label: t('actions.managePlan', { ns: 'team' }),
                                onClick: () => {
                                  setManagingTeam(team)
                                  setShowManagePlanFlow(true)
                                },
                                disabled: !team.order,
                              },
                            ]
                          : []),
                        {
                          label: t('actions.unlinkTeam', { ns: 'team' }),
                          onClick: () => handleUnbindTeam(team),
                          type: 'danger',
                          disabled: !canUnbind || isUnbinding,
                        },
                      ]}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Action Buttons - Fixed at bottom */}
        {!currentTeamId && (
          <div className="px-4 pb-4">
            <div className="grid grid-cols-2 gap-4">
              <Button type="outline" size="sm" onClick={handleJoinTeamInMenu} className="t-x-sb">
                <Trans i18nKey="header.userDropdown.joinTeams">Join Teams</Trans>
              </Button>
              <Button type="outline" size="sm" onClick={handleCreateTeamInMenu} className="t-x-sb">
                <Trans i18nKey="header.userDropdown.createTeams">Create Teams</Trans>
              </Button>
            </div>
          </div>
        )}
      </div>

      {showRenewModal && renewingTeam && (
        <TeamRenewModal
          open={showRenewModal}
          team={renewingTeam}
          onClose={() => {
            setShowRenewModal(false)
            setRenewingTeam(null)
          }}
          onSuccess={() => {
            setShowRenewModal(false)
            setRenewingTeam(null)
            queryClient.invalidateQueries({ queryKey: ['teamList'] })
          }}
        />
      )}

      {showManagePlanFlow && managingTeam && (
        <TeamManagePlanFlow
          open={showManagePlanFlow}
          team={managingTeam}
          onClose={() => {
            setShowManagePlanFlow(false)
            setManagingTeam(null)
          }}
          onSuccess={() => {
            setShowManagePlanFlow(false)
            setManagingTeam(null)
            message.success(t('managePlan.success', { ns: 'team' }))
            queryClient.invalidateQueries({ queryKey: ['teamList'] })
          }}
        />
      )}
    </div>
  )
}
